import React, { useState, useRef } from "react";
import styled from "styled-components";
import TopMenu from "./TopMenu";
import TabNavigation from "./TabNavigation";
import SurveyQuestionsTab from "./SurveyQuestionsTab";
import FactorSettingsTab from "./FactorSettingsTab";
import LanguagesTab from "./LanguagesTab";
import SettingsDrawer from "./SettingsDrawer";
import AISettings from "./AISettings";
import SettingsModal from "./SettingsModal";
import { componentTypes } from "./componentTypes";
import { CopilotProvider } from "../CopilotV2/CopilotContext";

// Helper functions (groupComponentTypesByCategory, reorder, getPrecedingQuestions) remain unchanged

function groupComponentTypesByCategory(typesObj) {
  const grouped = {};
  Object.entries(typesObj).forEach(([key, def]) => {
    const cat = def.category || "core"; // default if missing
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(key);
  });
  return grouped;
}

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function getPrecedingQuestions(droppedItems, selectedItem) {
  if (!selectedItem) return [];
  const currentIndex = droppedItems.findIndex(
    (x) => x.uniqueId === selectedItem.uniqueId
  );
  const preceding = droppedItems.slice(0, currentIndex);
  return preceding.map((item) => {
    const label =
      item.properties.label ||
      item.properties.placeholder ||
      `Question ${item.uniqueId}`;
    return {
      key: item.uniqueId,
      value: item.uniqueId,
      text: label,
      type: item.type,
      scale: item.properties.scale,
      options: item.properties.options,
    };
  });
}

const Container = styled.div`
  width: 100%;
`;

// Define initial categories
const initialCategories = [
  { name: "Colors", options: ["Red", "Blue", "Green"], type: "text" },
  { name: "Sizes", options: ["Small", "Medium", "Large"], type: "text" },
  { name: "Event Date", options: [], type: "date" },
];

const SurveyBuilder = () => {
  // Primary state
  const [droppedItems, setDroppedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [factors, setFactors] = useState([]);
  const [aggregateFollowUps, setAggregateFollowUps] = useState([]);
  // Add shared categories state
  const [categories, setCategories] = useState(initialCategories);
  
  // Use useRef to maintain a stable reference to the counter that persists between renders
  const uniqueIdCounterRef = useRef(1);

  // Languages & translations
  const [languageCodes, setLanguageCodes] = useState([]);
  const [globalDictionary, setGlobalDictionary] = useState({});

  // Add state to track end screen items
  const [endScreenItems, setEndScreenItems] = useState([]);

  // Persist AI settings in parent state
  const [aiOptions, setAIOptions] = useState({
    selectedTone: "balanced",
    agentActive: false,
    maxQuestions: 5,
    agentGoal: "",
    agentContext: "",
    memoryEnabled: false,
  });

  // Add settings modal state and theme settings
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [themeSettings, setThemeSettings] = useState({
    buttonColor: "#2d70e2",
    highlightText: "#2d70e2",
    topBanner: "transparent",
    secondaryButton: "#EBF1FD",
    backgroundColor: "white",
    logoUrl: ""
  });

  console.log({
    questions: droppedItems,
    factors: factors,
    aggregateFactors: aggregateFollowUps,
    languageCodes: languageCodes,
    globalDictionary: globalDictionary,
    aiOptions: aiOptions,
    themeSettings: themeSettings,
    endScreenItems: endScreenItems,
  });

  // Tab navigation state
  const [activeTab, setActiveTab] = useState("surveyQuestions");

  const handlePreview = () => { /* ... */ };
  const handleSaveTemplate = () => { /* ... */ };
  
  // Update handleSettings to open the settings modal
  const handleSettings = () => {
    setIsSettingsModalOpen(true);
  };

  // Handle saving settings from the modal
  const handleSaveSettings = (settings) => {
    console.log("Saving survey theme settings:", settings);
    setThemeSettings(settings);
  };

  const skipOptions = getPrecedingQuestions(droppedItems, selectedItem);

  // Updated setSelectedItem to add debugging
  const handleSetSelectedItem = (item) => {
    console.log("SurveyBuilder: Setting selected item:", item ? `${item.uniqueId} (${item.type})` : 'null');
    setSelectedItem(item);
  };

  const handleCloseDrawer = () => {
    console.log("SurveyBuilder: Closing drawer, clearing selected item");
    setSelectedItem(null);
  };
  
  // Use useRef instead of useState to prevent re-renders
  const surveyQuestionsTabRef = useRef(null);
  
  const handleDeleteItem = () => {
    if (!selectedItem) {
      console.log("No item selected for deletion");
      return;
    }
    
    console.log("Attempting to delete item:", selectedItem.uniqueId, "type:", selectedItem.type);
    
    if (selectedItem.type === 'end_screen') {
      // If it's an end screen, use the specialized method
      console.log("Item is an end screen, delegating to removeSelectedEndScreen");
      if (surveyQuestionsTabRef.current) {
        surveyQuestionsTabRef.current.removeSelectedEndScreen();
      }
    } else {
      // Otherwise it's a regular component - remove ONLY this specific item by uniqueId
      console.log("Removing regular component with ID:", selectedItem.uniqueId);
      
      // Get the current items and filter out just the one with matching ID
      const updatedItems = droppedItems.filter(item => item.uniqueId !== selectedItem.uniqueId);
      
      // Update state with filtered items
      setDroppedItems(updatedItems);
    }
    
    // Clear the selected item
    setSelectedItem(null);
  };

  const groupedTypes = groupComponentTypesByCategory(componentTypes);

  // Handle updating end screen items from the SurveyQuestionsTab
  const handleUpdateEndScreenItems = (items) => {
    setEndScreenItems(items);
  };

  return (
    <Container>
      <TopMenu
        createdBy="Esther Howard"
        type="Grant Form"
        timeToComplete="5 - 10 min"
        onPreview={handlePreview}
        onSaveTemplate={handleSaveTemplate}
        onSettings={handleSettings}
      />

      <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === "surveyQuestions" && (
        <SurveyQuestionsTab
          droppedItems={droppedItems}
          setDroppedItems={setDroppedItems}
          selectedItem={selectedItem}
          setSelectedItem={handleSetSelectedItem}
          groupedTypes={groupedTypes}
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          reorder={reorder}
          componentTypes={componentTypes}
          uniqueIdCounterRef={uniqueIdCounterRef}
          factors={factors}
          categories={categories}
          setCategories={setCategories}
          endScreenItems={endScreenItems}
          setEndScreenItems={handleUpdateEndScreenItems}
          ref={surveyQuestionsTabRef}
        />
      )}

      {activeTab === "factorSettings" && (
        <FactorSettingsTab
          droppedItems={droppedItems}
          setDroppedItems={setDroppedItems}
          factors={factors}
          setFactors={setFactors}
          aggregateFollowUps={aggregateFollowUps}
          setAggregateFollowUps={setAggregateFollowUps}
        />
      )}

      {activeTab === "languages" && (
        <LanguagesTab
          questions={droppedItems}
          factors={factors}
          aggregateFactors={aggregateFollowUps}
          languageCodes={languageCodes}
          setLanguageCodes={setLanguageCodes}
          globalDictionary={globalDictionary}
          setGlobalDictionary={setGlobalDictionary}
        />
      )}

      {activeTab === "aisettings" && (
        <AISettings aiOptions={aiOptions} setAIOptions={setAIOptions} />
      )}

      {selectedItem && (
        <SettingsDrawer
          selectedItem={selectedItem}
          onClose={handleCloseDrawer}
          onDelete={handleDeleteItem}
          droppedItems={droppedItems}
          setDroppedItems={setDroppedItems}
          skipOptions={skipOptions}
          categories={categories}
          setCategories={setCategories}
        />
      )}

      {/* Add Settings Modal */}
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        themeSettings={themeSettings}
        onSaveSettings={handleSaveSettings}
      />
    </Container>
  );
};

// Export with CopilotProvider wrapped around SurveyBuilder for API access
const SurveyBuilderWithCopilot = () => (
  <CopilotProvider>
    <SurveyBuilder />
  </CopilotProvider>
);

export default SurveyBuilderWithCopilot;

